<template>
  <div class="lightbox" :class="{ 'lightbox--open': isVisibleInternal }">
    <div
      v-if="media && media.length > 0 && isVisibleInternal"
      ref="container"
      class="lightbox__container"
      :class="{
        'lightbox__container--visible': isVisibleInternal,
        // The activated flag is needed to not show the animation on...
        // initial load && on top gallery close
        'lightbox__container--leave-animation-active': isLeaveAnimationActive,
      }"
      :style="`background-color: ${backgroundColor}`"
      @animationend="activateLeaveAnimationIfNeeded"
    >
      <div class="lightbox__content">
        <div class="lightbox__header" @click.stop="closeLightbox">
          <button
            v-if="showCloseIconTop"
            class="lightbox__button-close lightbox__button-close--top icon-cross"
            :class="{ 'lightbox__button-close--dark-icons': isDarkIcons }"
            type="button"
            title="Close (Esc)"
          ></button>
          <IconClose v-else class="lightbox__button-close lightbox__button-close--bottom" />
        </div>
        <transition :name="transitionName">
          <div
            :key="currentMedium.url"
            ref="lightboxMediumContainer"
            :class="getClassIncludingType('lightbox__container-medium')"
          >
            <div :class="getClassIncludingType('lightbox__medium')" :style="containerStyles">
              <slot name="medium" v-bind:currentMedium="currentMedium">
                <img :src="currentMedium.url" :srcset="currentMedium.srcset || ''" />
              </slot>
            </div>
            <div class="lightbox__info">
              <slot name="caption" v-bind:currentMedium="currentMedium"></slot>
            </div>
          </div>
        </transition>
      </div>
      <button
        v-if="isNavigationButtonVisible"
        type="button"
        class="lightbox__arrow lightbox__left icon-arrow-left"
        :class="{ 'lightbox__arrow--dark-icons': isDarkIcons }"
        title="Previous"
        @click.stop="previousMedium()"
      ></button>
      <button
        v-if="isNavigationButtonVisible"
        type="button"
        class="lightbox__arrow lightbox__right icon-arrow-right"
        :class="{ 'lightbox__arrow--dark-icons': isDarkIcons }"
        title="Next"
        @click.stop="nextMedium()"
      ></button>
    </div>
  </div>
</template>

<script>
/**
 * This component is based on https://github.com/pexea12/vue-image-lightbox.
 */
import Hammer from 'hammerjs'
import { KEY_ESC, KEY_ARROW_LEFT, KEY_ARROW_RIGHT } from '@/helpers/keyCodes'
import IconClose from '@/components/_icons/IconClose.svg'

const ANIMATION_ZOOM_NORMAL = 'zoom-normal'
const TRANSITION_NEXT = 'fade-next'
const TRANSITION_PREV = 'fade-prev'
const TRANSITION_NONE = ''

export default {
  name: 'Lightbox',
  components: { IconClose },
  props: {
    /**
     * Image array to display
     */
    media: {
      type: Array,
      required: true,
      validator(media) {
        return media.every(medium => ['type', 'url'].every(key => medium[key]))
      },
    },

    backgroundColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.9)',
    },

    containerHeight: {
      type: Number,
    },

    containerWidth: {
      type: Number,
    },

    isDarkIcons: {
      type: Boolean,
      default: false,
    },

    isSwipeEnabled: {
      type: Boolean,
      default: true,
    },

    /**
     * Controls lightbox visibility
     */
    isVisible: {
      type: Boolean,
      required: false,
    },
    /**
     * Index of the medium to be shown
     */
    currentMediumIndex: {
      type: Number,
      default: 0,
    },
    /**
     * Move to next medium automatically
     */
    autoPlay: {
      type: Boolean,
      default: false,
    },
    /**
     * Time to stop at an medium before move on to next medium
     */
    autoPlayTime: {
      type: Number,
      default: 3000,
    },
    /**
     * Set to `true` to avoid scrolling views behind lightbox
     */
    disableScroll: {
      type: Boolean,
      default: true,
    },
    /**
     * If scrollLeft && scrollTop are set, the lightbox medium container will be scrolled to the set values
     * This is obviously only possible if the lightbox medium container is bigger than the surrounding lightbox container
     */
    scrollLeft: {
      type: Number,
    },

    scrollTop: {
      type: Number,
    },

    /**
     * Shows/hides buttons to navigate between media
     */
    showNavigationButtons: {
      type: Boolean,
      default: true,
    },

    showCloseIconTop: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      // visibility and current medium can be controlled from outside (prop) and inside (data)
      // data is synced with prop via watchers
      // events are triggered when data changes to notify parent
      isVisibleInternal: false,
      isLeaveAnimationActive: false,
      currentMediumIndexInternal: 0,
      timer: null,
      transitionName: TRANSITION_NEXT,
      lastEvent: null,
    }
  },

  computed: {
    currentMedium() {
      return this.media[this.currentMediumIndexInternal]
    },

    isNavigationButtonVisible() {
      return this.media.length > 1 && this.showNavigationButtons
    },

    containerStyles() {
      return this.containerWidth && this.containerHeight
        ? `width: ${this.containerWidth}px; height: ${this.containerHeight}px`
        : ''
    },
  },

  watch: {
    isVisible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) {
          this.scrollLightboxContainer()

          // don't animate when showing lightbox
          this.transitionName = TRANSITION_NONE
          this.$nextTick(function () {
            this.transitionName = TRANSITION_NEXT
          })
        }
        this.isVisibleInternal = Boolean(isVisible && this.currentMedium)
      },
    },

    isVisibleInternal: {
      immediate: true,
      handler(isVisible) {
        if (document != null) {
          this.onToggleLightbox(isVisible)
          this.$emit('visibility-change', isVisible, this.lastEvent)
        }
        this.lastEvent = null

        if (isVisible) {
          this.$notifier.trackEvent({
            action: this.$notifier.ACTIONS.productInteraction,
            details: { action: 'lightbox-open' },
          })
        }
      },
    },

    currentMediumIndex: {
      immediate: true,
      handler(value) {
        if (this.media[value]) this.currentMediumIndexInternal = value
      },
    },

    currentMediumIndexInternal(value) {
      this.$emit('medium-change', value)
    },
  },

  mounted() {
    if (this.autoPlay) {
      this.timer = setInterval(() => {
        this.nextMedium()
      }, this.autoPlayTime)
    }
    if (this.$refs.container && this.isSwipeEnabled) {
      const hammer = new Hammer(this.$refs.container)
      hammer.on('swiperight', () => {
        this.previousMedium()
      })
      hammer.on('swipeleft', () => {
        this.nextMedium()
      })
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.addKeyEvent)
    if (this.autoPlay) {
      clearInterval(this.timer)
    }
  },

  methods: {
    activateLeaveAnimationIfNeeded($event) {
      this.isLeaveAnimationActive = $event.animationName.includes(ANIMATION_ZOOM_NORMAL)
    },

    onToggleLightbox(isVisible) {
      if (this.disableScroll) {
        document.querySelector('html').classList.toggle('no-scroll', isVisible)
      }
      document.querySelector('body').classList.toggle('lightbox--open', isVisible)
      if (isVisible) {
        document.addEventListener('keydown', this.addKeyEvent)
      } else {
        document.removeEventListener('keydown', this.addKeyEvent)
      }
    },

    addKeyEvent(event) {
      if (event.keyCode === KEY_ARROW_LEFT) this.previousMedium()
      if (event.keyCode === KEY_ARROW_RIGHT) this.nextMedium()
      if (event.keyCode === KEY_ESC) this.closeLightbox()
    },

    closeLightbox(e) {
      if (e) this.lastEvent = e
      this.isVisibleInternal = false
      this.$emit('lightbox-close')
    },

    scrollLightboxContainer() {
      if (!this.scrollLeft || !this.scrollTop) return
      this.$nextTick(() => {
        this.$refs.lightboxMediumContainer.scrollLeft = this.scrollLeft
        this.$refs.lightboxMediumContainer.scrollTop = this.scrollTop
      })
    },

    nextMedium() {
      this.transitionName = TRANSITION_NEXT
      this.currentMediumIndexInternal = (this.currentMediumIndexInternal + 1) % this.media.length
      this.scrollLightboxContainer()
    },

    previousMedium() {
      this.transitionName = TRANSITION_PREV
      this.currentMediumIndexInternal =
        (this.currentMediumIndexInternal + this.media.length - 1) % this.media.length
      this.scrollLightboxContainer()
    },

    getClassIncludingType(className) {
      return `${className} ${className}--${this.currentMedium.type}`
    },
  },
}
</script>

<style lang="scss">
.no-scroll {
  overflow-y: hidden;
}
</style>

<style lang="scss" scoped>
$button-dimension: 40px;
$button-margin: 20px;
$zoom-normal: zoom-normal;

.lightbox {
  pointer-events: none;

  &--open {
    pointer-events: inherit;
  }

  &__container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    opacity: 0;
    z-index: -1;

    &--leave-animation-active {
      animation: zoom-reverse;
      animation-duration: 0.4s;
    }

    &--visible {
      animation-duration: 0.2s;
      animation-name: zoom-normal;
      animation-direction: normal;
      animation-fill-mode: forwards;
      opacity: 1;
    }
  }

  &__content {
    position: relative;
    width: 100%;
    height: 100%;
    margin: 0;
  }

  &__header {
    position: absolute;
    right: 0;
    top: 0;
    height: 40px;
    z-index: 1;
  }

  &__container-medium {
    position: absolute;
    top: 50%;
    width: 100%;
    max-height: 100%;
    margin: 0;
    transform: translateY(-50%);

    &--video {
      height: 100%;
    }
  }

  &__medium {
    /deep/ * {
      display: block;
      width: 100%;
      max-width: 100%;
      max-height: 100%;
      margin: 0 auto;
    }

    &--image {
      /deep/ * {
        user-select: none;
        pointer-events: none;
      }
    }

    &--video {
      height: 100%;

      /deep/ * {
        height: 100%;
      }
    }
  }

  &__info {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: auto;
  }

  &__button-close,
  &__arrow {
    color: $color-white;
    cursor: pointer;

    &--dark-icons {
      color: $color-grey-dark-2;
    }
  }

  &__button-close {
    height: $button-dimension;
    width: $button-dimension;

    &--top {
      padding: 10px;
      margin: $button-margin;
    }

    &--bottom {
      position: fixed;
      right: 16px;
      bottom: 16px;
      background-color: $color-white;
      border-radius: 50%;
    }
  }

  &__button-close:hover {
    opacity: 0.8;
  }

  &__arrow {
    position: absolute;
    top: 50%;
    display: none;
    width: $button-dimension;
    height: $button-dimension;
    margin: -$button-dimension $button-margin 0 $button-margin;
    padding: 0;
    color: $color-black;
    background-color: $color-white;
    border-radius: 100%;
    opacity: 0.4;
    -webkit-touch-callout: none;
    user-select: none;

    @media ($mq-large) {
      display: block;
    }
  }

  &__arrow:hover {
    opacity: 0.6;
  }

  &__left {
    left: 0;
    padding-right: 2px;
  }

  &__right {
    right: 0;
    padding-left: 2px;
  }

  &--open {
    overflow: hidden;
  }

  .fade-next-enter {
    opacity: 1;
    transform: translateX(100%) translateY(-50%);
  }

  .fade-prev-enter {
    opacity: 1;
    transform: translateX(-100%) translateY(-50%);
  }

  .fade-next-enter-active,
  .fade-next-leave-active,
  .fade-prev-enter-active,
  .fade-prev-leave-active {
    transition: 0.5s ease transform;
  }

  .fade-next-leave-to {
    opacity: 1;
    transform: translateX(-100%) translateY(-50%);
  }

  .fade-prev-leave-to {
    opacity: 1;
    transform: translateX(100%) translateY(-50%);
  }

  .fade-next-enter {
    opacity: 1;
    transform: translateX(100%) translateY(-50%);
  }

  .fade-prev-enter {
    opacity: 1;
    transform: translateX(-100%) translateY(-50%);
  }
}

// Don't change animation name, it's referenced in the JS
@keyframes zoom-normal {
  from {
    z-index: 2000;
    opacity: 0;
    transform: scale3d(0.7, 0.7, 0.7);
  }

  100% {
    z-index: 2000;
    opacity: 1;
  }
}

@keyframes zoom-reverse {
  from {
    z-index: 2000;
    opacity: 1;
  }

  100% {
    opacity: 0;
    z-index: -1;
    transform: scale3d(0.7, 0.7, 0.7);
  }
}
</style>
